import { useState, useEffect, useCallback, useRef } from 'react';
import { appointmentApi } from '../api/appointmentApi';
import { 
  Appointment, 
  AppointmentFormData, 
  GetAppointmentsQueryParams, 
  AppointmentStatus,
  AppointmentType,
  PaginatedResponse
} from '../types/appointment.types';

type PriorityType = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

// Helper functions moved outside component to fix Fast Refresh
const getValidStatuses = (status: string | string[] | undefined): AppointmentStatus[] => {
  if (!status) return [];
  const statusArray = Array.isArray(status) ? status : [status];
  return statusArray.filter(s => 
    Object.values(AppointmentStatus).includes(s as AppointmentStatus)
  ) as AppointmentStatus[];
};

const getValidTypes = (type: string | string[] | undefined): AppointmentType[] | undefined => {
  if (!type) return undefined;
  const validTypes = new Set<AppointmentType>(['CONSULTATION', 'FOLLOW_UP', 'THERAPIES', 'OTC', 'WALK_IN', 'OTHER']);
  const typeArray = Array.isArray(type) ? type : [type];
  return typeArray.filter((t): t is AppointmentType => 
    validTypes.has(t as AppointmentType)
  );
};

const getValidPriorities = (priority: string | string[] | undefined): PriorityType[] | undefined => {
  if (!priority) return undefined;
  const validPriorities = new Set<PriorityType>(['LOW', 'MEDIUM', 'HIGH', 'URGENT']);
  const priorityArray = Array.isArray(priority) ? priority : [priority];
  return priorityArray.filter((p): p is PriorityType => 
    validPriorities.has(p as PriorityType)
  );
};

// Extended interface with all possible query parameters
export interface ExtendedAppointmentsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: AppointmentStatus[] | string | string[];
  type?: AppointmentType[] | string | string[];
  patientId?: string;
  doctorId?: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
  isWalkIn?: boolean;
  priority?: PriorityType[] | string | string[];
  [key: string]: any; // For any other dynamic parameters
}

// Helper function to get start and end of day in local time
const getStartAndEndOfDay = () => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  
  return { start, end };
};

// Interface for pagination state
interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Type for the setParams function
type SetParamsType = (
  newParams: Partial<ExtendedAppointmentsQueryParams> | 
  ((prev: ExtendedAppointmentsQueryParams) => Partial<ExtendedAppointmentsQueryParams>)
) => void;


// Interface for the hook's return type
interface UseAppointmentsReturn {
  appointments: Appointment[];
  loading: boolean; // Changed from isLoading to match the destructured name
  error: Error | null;
  pagination: PaginationState;
  params: ExtendedAppointmentsQueryParams;
  setParams: SetParamsType;
  fetchAppointments: (customParams?: Partial<ExtendedAppointmentsQueryParams>) => Promise<PaginatedResponse<Appointment>>;
  createAppointment: (data: AppointmentFormData) => Promise<Appointment>;
  updateAppointment: (id: string, updates: Partial<AppointmentFormData>) => Promise<Appointment>;
  deleteAppointment: (id: string) => Promise<void>;
  updateStatus: (id: string, status: AppointmentStatus) => Promise<Appointment>;
  handlePageChange: (event: unknown, newPage: number) => void;
  handleRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export const useAppointments = (initialParams: ExtendedAppointmentsQueryParams = {}): UseAppointmentsReturn => {
  // State for appointments
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  
  // State for loading and error
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Memoize the initial params to prevent unnecessary re-renders
  const initialParamsRef = useRef(initialParams);
  
  // Ref to hold current params for handlers
  const currentParamsRef = useRef<ExtendedAppointmentsQueryParams>();
  
  // State for query parameters with defaults
  const [params, setParamsState] = useState<ExtendedAppointmentsQueryParams>(() => {
    const { start, end } = getStartAndEndOfDay();
    
    return {
      page: 1, // 1-indexed for API
      limit: 30,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      ...initialParamsRef.current
    };
  });
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0, // 0-indexed for Material-UI
    limit: 30,
    total: 0,
    totalPages: 1
  });
  
  // Helper function to normalize query parameters with proper type safety
  const normalizeQueryParams = (params: ExtendedAppointmentsQueryParams): GetAppointmentsQueryParams => {
    // Create a new object to store the final result
    const result: Omit<GetAppointmentsQueryParams, 'status' | 'type' | 'priority'> & {
      status?: AppointmentStatus[];
      type?: AppointmentType[];
      priority?: PriorityType[];
    } = {};
    
    // Handle page and limit with proper type checking
    const page = Number(params.page);
    if (!isNaN(page) && page >= 1) {
      result.page = page;
    }
    
    const limit = Number(params.limit);
    if (!isNaN(limit) && limit >= 1 && limit <= 100) {
      result.limit = limit;
    }
    
    // Handle search with type safety
    if (params.search !== undefined && params.search !== null && params.search !== '') {
      result.search = String(params.search);
    }
    
    // Handle status with type safety
    if (params.status !== undefined) {
      const statusArray = Array.isArray(params.status) ? params.status : [params.status];
      result.status = statusArray.filter((s): s is AppointmentStatus => 
        Object.values(AppointmentStatus).includes(s as AppointmentStatus)
      );
    }
    
    // Handle type with type safety
    if (params.type !== undefined) {
      const typeArray = Array.isArray(params.type) ? params.type : [params.type];
      const validTypes = new Set<AppointmentType>(['CONSULTATION', 'FOLLOW_UP', 'THERAPIES', 'OTC', 'WALK_IN', 'OTHER']);
      result.type = typeArray.filter((t): t is AppointmentType => 
        validTypes.has(t as AppointmentType)
      );
    }
    
    // Handle priority with type safety
    if (params.priority !== undefined) {
      const priorityArray = Array.isArray(params.priority) ? params.priority : [params.priority];
      const validPriorities = new Set<PriorityType>(['LOW', 'MEDIUM', 'HIGH', 'URGENT']);
      result.priority = priorityArray.filter((p): p is PriorityType => 
        validPriorities.has(p as PriorityType)
      );
    }
    
    // Handle other fields with proper type safety
    if (params.patientId !== undefined && params.patientId !== '') {
      result.patientId = String(params.patientId);
    }
    
    if (params.doctorId !== undefined && params.doctorId !== '') {
      result.doctorId = String(params.doctorId);
    }
    
    if (params.locationId !== undefined && params.locationId !== '') {
      result.locationId = String(params.locationId);
    }
    
    if (params.startDate) {
      result.startDate = new Date(params.startDate).toISOString();
    }
    
    if (params.endDate) {
      result.endDate = new Date(params.endDate).toISOString();
    }
    
    if (params.isWalkIn !== undefined) {
      result.isWalkIn = Boolean(params.isWalkIn);
    }
    
    // Convert to the final type with proper type assertions
    return result as GetAppointmentsQueryParams;
  };

  // Set params with type safety
  const setParams = useCallback<SetParamsType>((paramsOrUpdater) => {
    setParamsState(prevParams => {
      let newParams: Partial<ExtendedAppointmentsQueryParams>;
      
      if (typeof paramsOrUpdater === 'function') {
        // Handle functional update
        newParams = paramsOrUpdater(prevParams);
      } else {
        // Handle direct object update
        newParams = paramsOrUpdater;
      }
      
      // Return the updated params without normalization to preserve all fields
      const updatedParams = {
        ...prevParams,
        ...newParams
      };
      
      // Update the ref with current params
      currentParamsRef.current = updatedParams;
      
      return updatedParams;
    });
  }, []);

  // Fetch appointments with current params
  const fetchAppointments = useCallback(async (customParams: Partial<ExtendedAppointmentsQueryParams> = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== FETCH APPOINTMENTS ===');
      console.log('Current params:', params);
      console.log('Custom params:', customParams);
      
      // Merge default, current, and custom params
      const mergedParams: ExtendedAppointmentsQueryParams = {
        ...params,
        ...customParams
      };
      
      console.log('Merged params:', mergedParams);
      

      // Convert to GetAppointmentsQueryParams with proper type safety
      const apiParams: GetAppointmentsQueryParams = {
        page: mergedParams.page || 1,
        limit: mergedParams.limit || pagination.limit || 30,
        search: mergedParams.search,
        status: getValidStatuses(mergedParams.status),
        type: getValidTypes(mergedParams.type),
        patientId: mergedParams.patientId,
        doctorId: mergedParams.doctorId,
        locationId: mergedParams.locationId,
        startDate: mergedParams.startDate,
        endDate: mergedParams.endDate,
        isWalkIn: mergedParams.isWalkIn,
        priority: getValidPriorities(mergedParams.priority),
      };
      
      console.log('API Params being sent:', JSON.stringify(apiParams, null, 2));
      
      // Make the API call with normalized parameters
      const response = await appointmentApi.getAll(apiParams);
      
      console.log('API Response:', JSON.stringify(response, null, 2));
      
      // Update appointments with force re-render
      const newAppointments = response.data || [];
      console.log('Setting appointments to:', newAppointments.length, 'items');
      setAppointments([...newAppointments]); // Force new array reference
      
      // Update pagination with the response meta data
      const newPagination: PaginationState = {
        page: response.meta?.page ? response.meta.page - 1 : 0, // Convert to 0-based for Material-UI
        limit: response.meta?.limit || apiParams.limit || pagination.limit || 30,
        total: response.meta?.total || 0,
        totalPages: response.meta?.totalPages || 1,
      };
      
      console.log('Current pagination:', pagination);
      console.log('New pagination from API:', newPagination);
      
      // Always update pagination to ensure UI reflects current state
      setPagination(newPagination);
      console.log('Updated pagination to:', newPagination);
      
      setError(null);
      return response;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'object' && err !== null && 'message' in err
          ? String(err.message)
          : 'Failed to fetch appointments';
      const error = new Error(errorMessage);
      setError(error);
      console.error('Error fetching appointments:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [params]); // Include all params in the dependency array to ensure we refetch when any param changes

  // Create a new appointment
  const createAppointment = useCallback(async (data: AppointmentFormData): Promise<Appointment> => {
    try {
      setLoading(true);
      const newAppointment = await appointmentApi.create(data);
      await fetchAppointments(); // Refresh the list with current pagination
      setError(null);
      return newAppointment;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'object' && err !== null && 'message' in err
          ? String(err.message)
          : 'Failed to create appointment';
      const error = new Error(errorMessage);
      setError(error);
      console.error('Error creating appointment:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchAppointments]);

  // Update an existing appointment
  const updateAppointment = useCallback(async (id: string, updates: Partial<AppointmentFormData>): Promise<Appointment> => {
    try {
      setLoading(true);
      // Get the current appointment data
      const currentAppointment = appointments.find(appt => appt.id === id);
      if (!currentAppointment) {
        throw new Error('Appointment not found');
      }
      
      // Merge the updates with the current appointment data
      const updatedData: AppointmentFormData = {
        patientId: updates.patientId ?? currentAppointment.patientId,
        doctorId: updates.doctorId ?? currentAppointment.doctorId,
        locationId: updates.locationId ?? currentAppointment.locationId,
        date: updates.date ?? currentAppointment.date ?? new Date(currentAppointment.startTime).toISOString().split('T')[0],
        time: updates.time ?? currentAppointment.time ?? new Date(currentAppointment.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        duration: updates.duration ?? 30, // Default to 30 minutes if not specified
        type: updates.type ?? currentAppointment.type,
        status: updates.status ?? currentAppointment.status,
        reason: updates.reason ?? currentAppointment.reason,
        notes: updates.notes ?? currentAppointment.notes,
        isWalkIn: updates.isWalkIn ?? currentAppointment.isWalkIn ?? false,
        priority: updates.priority ?? currentAppointment.priority ?? 'MEDIUM',
      };
      
      const updatedAppointment = await appointmentApi.update(id, updatedData);
      await fetchAppointments({ 
        page: pagination.page, 
        limit: pagination.limit 
      }); // Refresh with current pagination
      return updatedAppointment;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'object' && err !== null && 'message' in err
          ? String(err.message)
          : 'Failed to update appointment';
      const error = new Error(errorMessage);
      setError(error);
      console.error('Error updating appointment:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [appointments, fetchAppointments, pagination.page, pagination.limit]);

  // Delete an appointment
  const deleteAppointment = useCallback(async (id: string): Promise<void> => {
    try {
      setLoading(true);
      await appointmentApi.delete(id);
      // Refresh the list but stay on the current page
      await fetchAppointments({ 
        page: Math.min(pagination.page, Math.max(1, pagination.totalPages - 1)),
        limit: pagination.limit 
      });
      setError(null);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'object' && err !== null && 'message' in err
          ? String(err.message)
          : 'Failed to delete appointment';
      const error = new Error(errorMessage);
      setError(error);
      console.error('Error deleting appointment:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchAppointments, pagination.page, pagination.limit, pagination.totalPages]);

  // Update appointment status
  const updateStatus = useCallback(async (id: string, status: AppointmentStatus): Promise<Appointment> => {
    try {
      setLoading(true);
      const updatedAppointment = await appointmentApi.updateStatus(id, status);
      // Refresh the list with current pagination
      await fetchAppointments({ 
        page: pagination.page + 1, // Convert to 1-based for API
        limit: pagination.limit 
      });
      setError(null);
      return updatedAppointment;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'object' && err !== null && 'message' in err
          ? String(err.message)
          : 'Failed to update appointment status';
      const error = new Error(errorMessage);
      setError(error);
      console.error('Error updating appointment status:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchAppointments, pagination.page, pagination.limit]);

  // Handle page change
  const handlePageChange = useCallback(async (_event: unknown, newPage: number) => {
    console.log('=== PAGE CHANGE HANDLER CALLED ===');
    console.log('Event:', _event);
    console.log('Current page (0-based):', newPage);
    console.log('API page (1-based):', newPage + 1);
    console.log('Current pagination state:', pagination);
    
    // Remove the page comparison check that's causing the issue
    console.log('Current pagination.page:', pagination.page, 'newPage:', newPage);
    // if (pagination.page === newPage) {
    //   console.log('Page unchanged, skipping fetch');
    //   return;
    // }
    
    // Validate that the requested page exists
    const requestedApiPage = newPage + 1;
    if (pagination.totalPages > 0 && requestedApiPage > pagination.totalPages) {
      console.log(`Page ${requestedApiPage} exceeds total pages ${pagination.totalPages}, skipping`);
      return;
    }
    
    // Update pagination state immediately for UI responsiveness
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
    
    console.log('Updated pagination state to page:', newPage);
    
    // Get current params from ref to avoid stale closure
    const currentParams = currentParamsRef.current || params;
    console.log('Current params:', currentParams);
    
    const newParams = {
      ...currentParams,
      page: requestedApiPage // Convert to 1-based for API
    };
    
    console.log('New params:', newParams);
    
    // Update params and fetch
    setParams(newParams);
    
    try {
      await fetchAppointments(newParams);
      console.log('Page change fetch completed successfully');
    } catch (error) {
      console.error('Page change fetch failed:', error);
      // Revert pagination state on error
      setPagination(prev => ({
        ...prev,
        page: pagination.page
      }));
      // Revert params on error
      setParams(currentParams);
    }
  }, [pagination.page, pagination.totalPages, params, setParams, fetchAppointments]);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    console.log('=== ROWS PER PAGE CHANGE ===');
    console.log('New limit:', newLimit);
    
    // Update pagination state immediately for UI responsiveness
    setPagination(prev => ({
      ...prev,
      page: 0, // Reset to first page when changing rows per page
      limit: newLimit
    }));
    
    // Get current params from ref to avoid stale closure
    const currentParams = currentParamsRef.current || params;
    console.log('Current params:', currentParams);
    
    const newParams = {
      ...currentParams,
      limit: newLimit,
      page: 1 // Reset to first page when changing rows per page
    };
    
    console.log('New params:', newParams);
    
    // Update params and fetch
    setParams(newParams);
    
    try {
      await fetchAppointments(newParams);
      console.log('Rows per page change fetch completed successfully');
    } catch (error) {
      console.error('Rows per page change fetch failed:', error);
      // Revert pagination state on error
      setPagination(prev => ({
        ...prev,
        page: pagination.page,
        limit: pagination.limit
      }));
      // Revert params on error
      setParams(currentParams);
    }
  }, [params, setParams, fetchAppointments, pagination.page, pagination.limit]);

  return {
    appointments,
    loading,
    error,
    pagination,
    params,
    setParams,
    fetchAppointments,
    createAppointment,
    updateAppointment,
    deleteAppointment,
    updateStatus,
    handlePageChange,
    handleRowsPerPageChange,
  };
};

export const useAppointment = (id: string) => {
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAppointment = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await appointmentApi.getById(id);
      setAppointment(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch appointment'));
      console.error('Error fetching appointment:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  return {
    appointment,
    loading,
    error,
    refetch: fetchAppointment,
  };
};
